import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { PartnerAsset, AssetPage } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2,
  ExternalLink,
  Download,
  FileText,
  Search,
  X,
} from 'lucide-react';

interface AssetsPageProps {
  page: AssetPage;
  title: string;
  description: string;
}

// Helper to format file size
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper to get file type icon/label
function getFileTypeLabel(mimeType: string | null): string {
  if (!mimeType) return 'File';
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PPT';
  if (mimeType.includes('image')) return 'Image';
  if (mimeType.includes('video')) return 'Video';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'ZIP';
  return 'File';
}

export function AssetsPage({ page, title, description }: AssetsPageProps) {
  const [assets, setAssets] = useState<PartnerAsset[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    loadAssets();
  }, [page]);

  const loadAssets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getAssets(page);
      setAssets(response.assets);
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to load assets:', err);
      setError('Failed to load resources');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (asset: PartnerAsset) => {
    if (asset.type !== 'file' || !asset.s3_key) return;

    setDownloadingId(asset.id);
    try {
      const response = await api.getAssetDownloadUrl(page, asset.id);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = response.file_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to get download URL:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Filter assets by search query and category
  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      !searchQuery ||
      asset.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (asset.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());

    const matchesCategory =
      !selectedCategory || asset.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Group assets by category
  const assetsByCategory = filteredAssets.reduce((acc, asset) => {
    const category = asset.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(asset);
    return acc;
  }, {} as Record<string, PartnerAsset[]>);

  const sortedCategories = Object.keys(assetsByCategory).sort((a, b) => {
    if (a === 'General') return 1;
    if (b === 'General') return -1;
    return a.localeCompare(b);
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-600 mt-1">{description}</p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                All
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!error && assets.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No resources available yet.</p>
              <p className="text-gray-400 text-sm mt-1">
                Check back later for new content.
              </p>
            </CardContent>
          </Card>
        )}

        {/* No Results State */}
        {!error && assets.length > 0 && filteredAssets.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No resources match your search.</p>
              <Button
                variant="link"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                }}
              >
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Assets by Category */}
        {sortedCategories.map((category) => (
          <div key={category} className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{category}</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {assetsByCategory[category].map((asset) => (
                <Card
                  key={asset.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    if (asset.type === 'external_link' && asset.external_url) {
                      handleExternalLink(asset.external_url);
                    } else if (asset.type === 'file') {
                      handleDownload(asset);
                    }
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg leading-tight">
                        {asset.title}
                      </CardTitle>
                      {asset.type === 'external_link' ? (
                        <ExternalLink className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                      ) : (
                        <Download className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    {asset.description && (
                      <CardDescription className="line-clamp-2">
                        {asset.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between text-sm">
                      {asset.type === 'file' ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100">
                            {getFileTypeLabel(asset.mime_type)}
                          </span>
                          {asset.file_size && (
                            <span>{formatFileSize(asset.file_size)}</span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Link
                        </span>
                      )}
                      {downloadingId === asset.id && (
                        <Loader2 className="h-4 w-4 animate-spin text-abby-primary" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
